import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Indicator } from '@bumble/app/components/Indicator';
import { Loading } from '@bumble/app/components/Loading';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { PageLoading } from '@bumble/app/components/PageLoading';
import SetContacts from '@bumble/app/components/SetContacts';
import SplitPanes from '@bumble/app/components/SplitPanes';
import { TestReachability } from '@bumble/app/components/TestReachability';
import {
  AppServiceCreate,
  AppServiceDescEdit,
  AppServiceEdit,
  AppServiceNameEdit,
  AppServiceResize,
  AppServicesTabDetails,
  AppServicesTabEndpoints,
  AppServicesTabHosting,
  AppServicesTabTalker,
} from '@bumble/app/components/app-service';
import { useApi } from '@bumble/app/hooks/use-api';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditIcon from '@mui/icons-material/Edit';
import ExpandOutlinedIcon from '@mui/icons-material/ExpandOutlined';
import NetworkPingIcon from '@mui/icons-material/NetworkPing';
import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const columns: GridColDef[] = [
  {
    field: 'app_service_name',
    headerName: 'Service Name',
    flex: 1,
    minWidth: 300,
    disableColumnMenu: true,
  },
  {
    field: 'app_service_id',
    headerName: 'Service ID',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'deployment',
    headerName: 'Deployment',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 150,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'size',
    headerName: 'Size',
    width: 150,
    disableColumnMenu: true,
  },
  {
    field: 'health',
    headerName: 'Health',
    width: 150,
    disableColumnMenu: true,
  },
  //  {
  //    field: 'fqdn',
  //    headerName: 'App Onprem DNS or IP',
  //    width: 450,
  //    disableColumnMenu: true,
  //  },
  {
    field: 'proto',
    headerName: 'App protocol',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'port',
    headerName: 'App port',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
  },
];

const columnVisible = {
  app_service_name: true,
  app_service_id: true,
  deployment: true,
  admin_state: true,
  op_state: true,
  health: true,
  // fqdn: true,
  proto: true,
  port: true,
};

export const AppServices = () => {
  const [loadingModelOpen, setLoadingModalOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['appServices'],
    api.listAppServiceApiV1ListAppServiceGet.bind(null, {}),
    { enabled: true },
  );

  const [showReachability, setShowReachability] = useState<boolean>(false);
  const [showNameDialog, setShowNameDialog] = useState<boolean>(false);
  const [showDescDialog, setShowDescDialog] = useState<boolean>(false);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showResizeDialog, setShowResizeDialog] = useState<boolean>(false);
  const [showContacts, setShowContacts] = useState(false);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed App Services.');
  };

  const removeAppService = useMutation(api.removeAppServiceApiV1RemoveAppServicePost, {
    onSuccess() {
      toast.success('App Service removed.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
      setLoadingModalOpen(false);
    },
  });

  const handleAppServiceDelete = () => {
    setLoadingModalOpen(true);
    removeAppService.mutate({
      app_service_id: selectedItemId,
    });
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const handleAppServiceLatency = () => {
    setShowReachability(true);
  };

  const handleEditName = () => {
    setShowNameDialog(true);
  };

  const handleEditDesc = () => {
    setShowDescDialog(true);
  };

  const handleEdit = () => {
    setShowEditDialog(true);
  };

  const {
    // isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['appServiceDetail'],
    api.getAppServiceDetailApiV1GetAppServiceDetailGet.bind(null, {
      app_service_id: selectedRowItem,
    }),
    { enabled: false },
  );

  useEffect(() => {
    if (selectedRowItem) {
      detailRefetch();
    }
  }, [detailRefetch, selectedRowItem]);

  return (
    <PageContent>
      <PageHeader pageName="App Services" count={data?.data?.length || 0} />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.app_service_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create App Service"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  // getRowId={(row: AppService) => row?.app_service_id}
                  refreshData={handleDataRefetch}
                  menuItems={[
                    {
                      label: 'Edit Name',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: handleEditName,
                    },
                    {
                      label: 'Edit Description',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: handleEditDesc,
                    },
                    {
                      label: 'Edit App Service',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: handleEdit,
                    },
                    {
                      label: 'Test Reachability',
                      icon: <NetworkPingIcon color="info" fontSize="small" />,
                      onClick: handleAppServiceLatency,
                    },
                    {
                      label: 'Setup Contacts',
                      icon: <PermContactCalendarOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowContacts(true),
                    },
                    {
                      label: 'Resize App Service',
                      icon: <ExpandOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowResizeDialog(true),
                      disabled: detailData?.data?.deployment === 'onprem',
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                      onClick: () => setShowConfirmDelete(true),
                      hasDivider: true,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={[
                      {
                        el: <AppServicesTabDetails data={detailData?.data} />,
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: <AppServicesTabEndpoints id={selectedItemId ?? ''} />,
                        label: 'Endpoints',
                        id: 1,
                      },
                      {
                        el: <AppServicesTabTalker id={selectedItemId ?? ''} />,
                        label: 'Top Talkers',
                        id: 2,
                      },
                      {
                        el: <AppServicesTabHosting id={selectedItemId ?? ''} />,
                        label: 'Multi-Hosting',
                        id: 3,
                      },
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create App Service"
        >
          <AppServiceCreate handleClose={handleCreateModalClose} />
        </CreateModal>
      )}
      {showReachability && selectedItemId && detailData && (
        <DialogStandard
          open={showReachability}
          handleClose={() => setShowReachability(false)}
          title="Test Reachability"
        >
          <TestReachability id={selectedItemId} type="service" />
        </DialogStandard>
      )}
      {showNameDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showNameDialog}
          handleClose={() => setShowNameDialog(false)}
          title="Edit Name"
        >
          <AppServiceNameEdit
            onSubmit={() => {
              setShowNameDialog(false);
              refetch();
              detailRefetch();
            }}
            appServiceId={selectedItemId}
            appServiceName={detailData?.data?.app_service_name}
          />
        </DialogStandard>
      )}
      {showDescDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showDescDialog}
          handleClose={() => setShowDescDialog(false)}
          title="Edit Description"
        >
          <AppServiceDescEdit
            onSubmit={() => {
              setShowDescDialog(false);
              refetch();
              detailRefetch();
            }}
            appServiceId={selectedItemId}
            appServiceDesc={detailData?.data?.description || ''}
          />
        </DialogStandard>
      )}
      {showEditDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditDialog}
          handleClose={() => setShowEditDialog(false)}
          title="Edit App Service"
        >
          <AppServiceEdit
            onSubmit={() => {
              setShowEditDialog(false);
              refetch();
              detailRefetch();
            }}
            appServiceId={selectedItemId}
            proto={detailData?.data?.proto}
            port={detailData?.data?.port}
            fqdn={detailData?.data?.fqdn}
          // serviceDownTimeout={detailData?.data?.service_down_timeout}
          />
        </DialogStandard>
      )}
      {showContacts && selectedItemId && detailData && (
        <DialogStandard
          open={showContacts}
          handleClose={() => setShowContacts(false)}
          title="Setup Contacts"
        >
          <SetContacts
            type='service'
            id={selectedItemId || ''}
            primaryContact={detailData?.data?.primary_contact_id || ''}
            secondaryContact={detailData?.data?.secondary_contact_id || ''}
            onSubmit={() => {
              setShowContacts(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleAppServiceDelete}
          title="Delete App Service?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of the app service.`}
          confirmationPhrase={selectedItemId}
        />
      )}
      {showResizeDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showResizeDialog}
          handleClose={() => setShowResizeDialog(false)}
          title="Resize Endpoints"
        >
          <AppServiceResize
            id={selectedItemId}
            onSubmit={() => {
              setShowResizeDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {loadingModelOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
};
